/**
 * Compatibility Rules v1 seed data.
 * Evidence-backed only. Do not infer from model names, screen size, or port existence.
 */

const VERIFIED_AT = '2026-09-01';

const EVIDENCE = {
  plusSystemPage: {
    sourceUrl: 'https://www.garmin.com/en-US/p/806410/pn/010-02706-00/',
    sourceType: 'GARMIN_PRODUCT_PAGE',
    verifiedAt: VERIFIED_AT,
  },
  ls2Lvs44Page: {
    sourceUrl: 'https://www.garmin.com/en-US/p/1908313/pn/010-03899-10/',
    sourceType: 'GARMIN_PRODUCT_PAGE',
    verifiedAt: VERIFIED_AT,
  },
  ls2Lvs42hdPage: {
    sourceUrl: 'https://www.garmin.com/en-US/p/1908313/pn/010-03899-00/',
    sourceType: 'GARMIN_PRODUCT_PAGE',
    verifiedAt: VERIFIED_AT,
  },
  ls2Install: {
    sourceUrl:
      'https://www8.garmin.com/manuals/webhelp/GUID-9C83E43A-D15D-4D48-9AF6-AF0CBFA49D98/EN-US/LiveScope_2_LVS42___LVS44_Install_EN-US.pdf',
    sourceType: 'GARMIN_INSTALLATION_MANUAL',
    verifiedAt: VERIFIED_AT,
  },
  plusInstall: {
    sourceUrl: 'https://static.garmin.com/pumac/Panoptix_Livescope_Install_EN.pdf',
    sourceType: 'GARMIN_INSTALLATION_MANUAL',
    verifiedAt: VERIFIED_AT,
  },
  transducerGuide2025: {
    sourceUrl: 'https://static.garmincdn.com/shared/nordic/catalogs/Garmin_EMEA_Transducer_Selection_Guide_2025.pdf',
    sourceType: 'GARMIN_TRANSDUCER_GUIDE',
    verifiedAt: VERIFIED_AT,
  },
};

/**
 * MVP displays whose hardware families appear on both:
 * - Garmin LiveScope Plus System Compatible Devices
 * - Garmin LiveScope 2 LVS44 Compatible Devices
 * Family-level because commercial/map variants share the same hardware compatibility.
 */
const MVP_DISPLAY_FAMILIES_PLUS_AND_LS2 = [
  { family: 'echomap-uhd2-6', label: 'ECHOMAP UHD2 6" sv' },
  { family: 'echomap-uhd2-7', label: 'ECHOMAP UHD2 7" sv' },
  { family: 'echomap-uhd2-9', label: 'ECHOMAP UHD2 9" sv' },
  { family: 'echomap-ultra-10', label: 'ECHOMAP Ultra 106sv' },
  { family: 'echomap-ultra-12', label: 'ECHOMAP Ultra 126sv' },
  { family: 'echomap-ultra-2-10', label: 'ECHOMAP Ultra 2 10"' },
  { family: 'echomap-ultra-2-12', label: 'ECHOMAP Ultra 2 12"' },
  { family: 'gpsmap-8400xsv-9', label: 'GPSMAP 943xsv (x3 9-inch family)' },
  { family: 'gpsmap-8400xsv-12', label: 'GPSMAP 1243xsv (x3 12-inch family)' },
  { family: 'gpsmap-8600xsv-10', label: 'GPSMAP 8610xsv' },
  { family: 'gpsmap-8600xsv-12', label: 'GPSMAP 8612xsv' },
  { family: 'gpsmap-9000xsv-10', label: 'GPSMAP 9210xsv / 9000xsv 10-inch family' },
  { family: 'gpsmap-9000xsv-13', label: 'GPSMAP 9213xsv / 9000xsv 13-inch family' },
];

/**
 * Legacy displays listed on Garmin LiveScope Plus Compatible Devices and absent
 * from Garmin LiveScope 2 LVS44 Compatible Devices.
 */
const LEGACY_PLUS_ONLY_DISPLAY_FAMILIES = [
  { family: 'gpsmap-7400xsv-10', label: 'GPSMAP 1042xsv' },
  { family: 'gpsmap-7400xsv-12', label: 'GPSMAP 1242xsv' },
];

const NETWORK_GENERATION_UPDATES = [
  {
    familySlug: 'garmin-livescope-2',
    networkGeneration: 'GARMIN_BLUENET',
    sourceUrl: EVIDENCE.ls2Install.sourceUrl,
  },
  {
    familySlug: 'garmin-livescope-2-hd',
    networkGeneration: 'GARMIN_BLUENET',
    sourceUrl: EVIDENCE.ls2Install.sourceUrl,
  },
  {
    familySlug: 'gpsmap-9000xsv-10',
    networkGeneration: 'GARMIN_BLUENET',
    sourceUrl:
      'https://www8.garmin.com/manuals/webhelp/GUID-7A16E3BC-8AE9-4DE5-BA5B-96277E90F307/EN-US/GPSMAP_9000xsv_Install_EN-US.pdf',
  },
  {
    familySlug: 'gpsmap-9000xsv-13',
    networkGeneration: 'GARMIN_BLUENET',
    sourceUrl:
      'https://www8.garmin.com/manuals/webhelp/GUID-7A16E3BC-8AE9-4DE5-BA5B-96277E90F307/EN-US/GPSMAP_9000xsv_Install_EN-US.pdf',
  },
];

function rule(partial) {
  return {
    subjectProductSlug: null,
    subjectHardwareFamilySlug: null,
    objectProductSlug: null,
    objectHardwareFamilySlug: null,
    requiredProductSlug: null,
    requiredHardwareFamilySlug: null,
    conditionData: null,
    ...partial,
  };
}

function buildCompatibilityRules() {
  const rules = [];

  rules.push(
    rule({
      ruleKey: 'requires:family:garmin-lvs32:product:garmin-gls-10',
      subjectHardwareFamilySlug: 'garmin-lvs32',
      objectProductSlug: 'garmin-gls-10',
      relationshipType: 'REQUIRES',
      verificationStatus: 'VERIFIED',
      ...EVIDENCE.plusInstall,
      notes:
        'Garmin Panoptix LiveScope installation instructions show LVS32 connected through the GLS 10 sonar module. LVS32 satisfies LIVE_SONAR only.',
    }),
    rule({
      ruleKey: 'requires:family:garmin-lvs34:product:garmin-gls-10',
      subjectHardwareFamilySlug: 'garmin-lvs34',
      objectProductSlug: 'garmin-gls-10',
      relationshipType: 'REQUIRES',
      verificationStatus: 'VERIFIED',
      ...EVIDENCE.plusSystemPage,
      notes:
        'Garmin LiveScope Plus System (010-02706-00) includes the GLS 10 sonar module with the LVS34 transducer. LVS34 and LVS34-IF share this family and do not include onboard processing.',
    }),
    rule({
      ruleKey: 'requires:family:garmin-lvs62:product:garmin-gls-10',
      subjectHardwareFamilySlug: 'garmin-lvs62',
      objectProductSlug: 'garmin-gls-10',
      relationshipType: 'REQUIRES',
      verificationStatus: 'VERIFIED',
      ...EVIDENCE.transducerGuide2025,
      notes:
        'Garmin Transducer Selection Guide 2025 describes LiveScope XR as a GLS 10 sonar black box with the LVS62 transducer. LVS62 satisfies LIVE_SONAR only.',
    })
  );

  for (const display of MVP_DISPLAY_FAMILIES_PLUS_AND_LS2) {
    rules.push(
      rule({
        ruleKey: `compatible_with:product:garmin-gls-10:family:${display.family}`,
        subjectProductSlug: 'garmin-gls-10',
        objectHardwareFamilySlug: display.family,
        relationshipType: 'COMPATIBLE_WITH',
        verificationStatus: 'VERIFIED',
        ...EVIDENCE.plusSystemPage,
        notes: `Garmin LiveScope Plus System Compatible Devices lists ${display.label}. Plus-generation transducers use this GLS 10 architecture.`,
        conditionData: { sonarArchitecture: 'LIVESCOPE_PLUS_GLS10' },
      }),
      rule({
        ruleKey: `compatible_with:family:garmin-livescope-2:family:${display.family}`,
        subjectHardwareFamilySlug: 'garmin-livescope-2',
        objectHardwareFamilySlug: display.family,
        relationshipType: 'COMPATIBLE_WITH',
        verificationStatus: 'VERIFIED',
        ...EVIDENCE.ls2Lvs44Page,
        notes: `Garmin LiveScope 2 LVS44 Compatible Devices lists ${display.label}. LiveScope 2 connects directly; no GLS 10 requirement.`,
        conditionData: {
          sonarArchitecture: 'LIVESCOPE_2',
          nativeNetwork: 'GARMIN_BLUENET',
          legacyMarineNetworkAdapterSku: 'UNVERIFIED',
        },
      }),
      rule({
        ruleKey: `compatible_with:family:garmin-livescope-2-hd:family:${display.family}`,
        subjectHardwareFamilySlug: 'garmin-livescope-2-hd',
        objectHardwareFamilySlug: display.family,
        relationshipType: 'COMPATIBLE_WITH',
        verificationStatus: 'VERIFIED',
        ...EVIDENCE.ls2Lvs42hdPage,
        notes: `Garmin LiveScope 2 product family (LVS42HD / LVS42HD-IF) shares the LVS44 Compatible Devices matrix on garmin.com product 1908313. Direct-connect; no GLS 10 requirement.`,
        conditionData: {
          sonarArchitecture: 'LIVESCOPE_2_HD',
          nativeNetwork: 'GARMIN_BLUENET',
          legacyMarineNetworkAdapterSku: 'UNVERIFIED',
        },
      })
    );
  }

  for (const display of LEGACY_PLUS_ONLY_DISPLAY_FAMILIES) {
    rules.push(
      rule({
        ruleKey: `compatible_with:product:garmin-gls-10:family:${display.family}`,
        subjectProductSlug: 'garmin-gls-10',
        objectHardwareFamilySlug: display.family,
        relationshipType: 'COMPATIBLE_WITH',
        verificationStatus: 'VERIFIED',
        ...EVIDENCE.plusSystemPage,
        notes: `Garmin LiveScope Plus System Compatible Devices lists ${display.label}.`,
        conditionData: { sonarArchitecture: 'LIVESCOPE_PLUS_GLS10' },
      }),
      rule({
        ruleKey: `incompatible_with:family:garmin-livescope-2:family:${display.family}`,
        subjectHardwareFamilySlug: 'garmin-livescope-2',
        objectHardwareFamilySlug: display.family,
        relationshipType: 'INCOMPATIBLE_WITH',
        verificationStatus: 'VERIFIED',
        ...EVIDENCE.ls2Lvs44Page,
        notes: `${display.label} is listed on Garmin LiveScope Plus System Compatible Devices and is absent from Garmin LiveScope 2 LVS44 Compatible Devices (retrieved ${VERIFIED_AT}).`,
      }),
      rule({
        ruleKey: `incompatible_with:family:garmin-livescope-2-hd:family:${display.family}`,
        subjectHardwareFamilySlug: 'garmin-livescope-2-hd',
        objectHardwareFamilySlug: display.family,
        relationshipType: 'INCOMPATIBLE_WITH',
        verificationStatus: 'VERIFIED',
        ...EVIDENCE.ls2Lvs42hdPage,
        notes: `${display.label} is listed on Garmin LiveScope Plus System Compatible Devices and is absent from the Garmin LiveScope 2 Compatible Devices matrix shared by LVS42HD (retrieved ${VERIFIED_AT}).`,
      })
    );
  }

  return rules;
}

const compatibilityRules = buildCompatibilityRules();

module.exports = {
  VERIFIED_AT,
  EVIDENCE,
  MVP_DISPLAY_FAMILIES_PLUS_AND_LS2,
  LEGACY_PLUS_ONLY_DISPLAY_FAMILIES,
  NETWORK_GENERATION_UPDATES,
  compatibilityRules,
  buildCompatibilityRules,
};
